import styled from "@emotion/styled";
import { useState } from "react";
import theme from "../../theme";
import userStorage from "../../utils/userStorage";
import SubHeader from "./subHeader";
import { Pagination } from "@mui/material";
import {
  DetailUserIcon,
  TrashCanIcon,
  WarningIcon,
} from "../../assets/icons/icons";
import AlertModal from "../../components/alertModal";
import { Nok } from "../../types/types";
import useGetNok from "../../hooks/useGetNok";
import NokRegisterModal from "../../components/nokRegisterModal/nokRegisterModal";
import useDeleteNok from "../../hooks/useDeleteNok";
import Spinner from "../../components/spinner";
import { SortArrow } from "../../components/sortArrow";
import { DotLoader } from "../../components/dotLoader";
import {
  formatDate,
  handleClickForSort,
  textSubstring,
} from "../../utils/util";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { PageLayout } from "../../components/PageLayout";
import { useSystemSetting } from "../../hooks/useSystemSetting";
import { useWindowSize } from "@reactuses/core";
import { MobileManageNok } from "./mobileManageNok";
import { PhoneMaskToggle } from "../../components/PhoneMaskToggle";
import NokDetailModal from "../../components/nokDetailModal/nokDetailModal";
import NokModifyModal from "../../components/nokModifyModal/nokModifyModal";
import { useI18n } from "../../locales";

const { md } = theme.screen;

export interface FetchParams {
  searchKey: string;
  groupId: string;
}

const ManageNok: React.FC<{}> = () => {
  const { t } = useI18n();
  const user = userStorage.get();
  const systemSetting = useSystemSetting();
  const { width } = useWindowSize();
  const [searchKey, setSearchKey] = useState("");
  const [page, setPage] = useState(0);
  const [openDelModal, setOpenDelModal] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [idx, setIdx] = useState(0);
  const [sort, setSort] = useState<string | undefined>("");
  const [detailData, setDetailData] = useState<Nok>();
  const [openDetail, setOpenDetail] = useState(false);
  const [openModify, setOpenModify] = useState(false);
  const [groupSubCode, setGroupSubCode] = useState(""); // 하위그룹코드(api용)

  const { data, isLoading } = useGetNok({
    page,
    searchKey,
    area: user?.area as string,
    sort,
    limit: 10,
    ...(groupSubCode &&
      groupSubCode !== "all" && { groupId: groupSubCode }),
  });

  const { mutate: deleteNok } = useDeleteNok({ seq: idx }, () =>
    setOpenDelModal(false)
  );

  const onClickBtn = ({ searchKey, groupId }: FetchParams) => {
    // 조회 버튼 클릭
    setPage(0);
    setSort("");
    setSearchKey(searchKey);
    setGroupSubCode(groupId);
  };

  const handlePageChange = (
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value - 1);
  };

  const openDeleteModal = (el: Nok) => {
    setIdx(el.seq);
    openNokDeleteModal();
  };

  const openNokDeleteModal = () => {
    setOpenDelModal(true);
  };

  const onPressYes = () => {
    deleteNok();
  };

  const onPressNo = () => {
    setOpenDelModal(false);
  };

  const onClickDetailIcon = (el: Nok) => {
    setDetailData(el);
    setOpenDetail(true);
  };

  const makeTableHeadList = (id: string, title: string, width: number) => {
    return (
      <TableHeaderList
        onClick={() => {
          handleClickForSort(id, setSort);
        }}
        width={width}
        disabled={data?.list.length === 0}
      >
        {title} <SortArrow id={id} state={sort} />
      </TableHeaderList>
    );
  };

  return (
    <PageLayout
      subHeader={
        width > md && (
          <SubHeader
            onClickBtn={onClickBtn}
            openModal={() => setOpenRegister(true)}
            systemSetting={systemSetting("nok")}
          />
        )
      }
      withBreadcrumb
    >
      {width <= md && (
        <MobileManageNok
          data={data}
          isLoading={isLoading}
          systemSetting={systemSetting("nok")}
          onClickBtn={onClickBtn}
          sort={sort}
          setSort={setSort}
          setOpenRegisterModal={setOpenRegister}
          onClickDeleteIcon={openDeleteModal}
          onClickDetailIcon={onClickDetailIcon}
        />
      )}
      {width > md && (
        <>
          <BodyHeader>
            <span style={{ marginRight: "5px" }}>
              {t.manageNok.recipientInfo} -{" "}
            </span>
            {isLoading && <DotLoader />}
            {!isLoading && data && (
              <span>{data.total_items + " " + t.manageNok.peopleUnit}</span>
            )}
            {!isLoading && !data && (
              <span>{`0 ${t.manageNok.peopleUnit}`}</span>
            )}
          </BodyHeader>
          <TableHeader>
            {makeTableHeadList(`name`, t.manageNok.nokName, 150)}
            {makeTableHeadList(`id`, t.manageNok.recipientContact, 170)}
            {makeTableHeadList(`related_name`, t.manageNok.ownerName, 150)}
            {makeTableHeadList(`related_id`, t.manageNok.ownerContact, 150)}
            {makeTableHeadList(`relation`, t.manageNok.relationship, 150)}
            {makeTableHeadList(
              `registered_at`,
              t.manageNok.accountCreationTime,
              150
            )}
          </TableHeader>
          <TableBody>
            {isLoading && <Spinner />}
            {data &&
              data.list.length > 0 &&
              data.list.map((el) => (
                <TableList key={el.seq.toString()}>
                  {/* 추가 알림 수신자이름 */}
                  <CustomTooltip
                    title={el?.name}
                    active={el?.name.length > 10}
                  >
                    <MobInfo
                      style={{
                        minWidth: "150px",
                      }}
                    >
                      {textSubstring(el.name, 0, 10)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 추가 알림 수신자연락처 */}
                  <MobInfo style={{ minWidth: "170px" }}>
                    <PhoneMaskToggle
                      text={el.id}
                      who={2} // 추가 알림 수신자
                      userIdx={user?.idx}
                      name={el.name}
                    />
                    {/* {el.id} */}
                  </MobInfo>

                  {/* 소유자이름 */}
                  <CustomTooltip
                    title={el?.related_name}
                    active={el?.related_name.length > 10}
                  >
                    <MobInfo
                      style={{
                        minWidth: "150px",
                        maxWidth: "150px",
                      }}
                    >
                      {textSubstring(el.related_name, 0, 10)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 소유자연락처 (소유자아이디) */}
                  <MobInfo style={{ minWidth: "150px" }}>
                    <PhoneMaskToggle
                      text={el.related_id}
                      who={1} // 소유자
                      userIdx={user?.idx}
                      name={el.related_name}
                    />
                    {/* {el.related_id} */}
                  </MobInfo>

                  {/* 관계 */}
                  <CustomTooltip
                    title={el?.relation}
                    active={el?.relation?.length > 10}
                  >
                    <MobInfo
                      style={{ minWidth: "150px", maxWidth: "150px" }}
                    >
                      {textSubstring(el.relation, 0, 10)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 계정 생성 일시 */}
                  <MobInfo style={{ minWidth: "150px" }}>
                    {formatDate(el.registered_at, "yyyy-MM-dd HH:mm")}
                  </MobInfo>

                  {/* 아이콘 */}
                  <IconBox>
                    {/* 정보 상세보기 아이콘 */}
                  <CustomTooltip title={t.manageNok.viewDetailInfo} active>
                      <IconWrapper onClick={() => onClickDetailIcon(el)}>
                        <DetailUserIcon />
                      </IconWrapper>
                    </CustomTooltip>

                  <CustomTooltip title={t.manageNok.deleteAccount} active>
                      <IconWrapper
                        onClick={() => {
                          if (!systemSetting("nok")?.delete) return;
                          openDeleteModal(el);
                        }}
                        disabled={!systemSetting("nok")?.delete}
                      >
                        <TrashCanIcon />
                      </IconWrapper>
                    </CustomTooltip>
                  </IconBox>
                </TableList>
              ))}
            {!isLoading && data && data.list.length === 0 && (
              <NoDataMassage>{t.manageNok.noDataFound}</NoDataMassage>
            )}
          </TableBody>
        </>
      )}

      {!isLoading && (
        <PaginationFooter>
          <Pagination
            count={data?.total_page}
            page={page + 1}
            onChange={handlePageChange}
            siblingCount={width <= md ? 1 : 3}
            size={width <= md ? "small" : "medium"}
          />
        </PaginationFooter>
      )}

      {openRegister && (
        <NokRegisterModal
          open={openRegister}
          onPressNo={() => setOpenRegister(false)}
        />
      )}
      <AlertModal
        open={openDelModal}
        onPressNo={onPressNo}
        title={t.manageNok.deleteConfirmTitle}
        content={t.manageNok.deleteConfirmContent}
        confirmText={t.common.delete}
        onPressYes={onPressYes}
        icon={<WarningIcon />}
      />
      {openDetail && detailData && (
        <NokDetailModal
          open={openDetail}
          data={detailData}
          onPressNo={() => setOpenDetail(false)}
          onClickModify={() => {
            setOpenModify(true);
          }}
        />
      )}
      {openModify && detailData && (
        <NokModifyModal
          data={detailData}
          open={openModify}
          onPressNo={() => setOpenModify(false)}
        />
      )}
    </PageLayout>
  );
};

const BodyHeader = styled.h1`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-bottom: 15px;
  border-bottom: 3px solid ${theme.color.neutral_200};
  display: flex;
  align-items: center;
`;

const TableHeader = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 30px;
  padding-left: 20px;
  padding-right: 20px;
`;

const TableHeaderList = styled.button<{ width: number }>`
  min-width: ${({ width }) => `${width}px`};
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  white-space: pre-line; /* \n 개행 허용 */
  text-align: center;
  color: ${theme.color.neutral_500};
  display: flex;
  justify-content: center;
  cursor: pointer;
  transition: 200ms;
  &:hover {
    font-weight: bold;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  flex: 1;
`;

const TableList = styled.div`
  background: ${theme.color.white};
  border-radius: 10px;
  width: 100%;
  height: 46px;
  display: flex;
  gap: 30px;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
`;

const MobInfo = styled.span`
  display: block;
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.neutral_600};
  text-align: center;
  white-space: normal;
`;

const IconBox = styled.div`
  display: flex;
  gap: 15px;
  flex: 1;
  justify-content: flex-end;
  padding-right: 33px;
`;

const IconWrapper = styled.button<{ disabled?: boolean }>`
  width: 23px;
  height: 23px;
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "defalut" : "pointer")};
  transition: 200ms;
  &:hover {
    scale: ${({ disabled }) => (disabled ? "100%" : "120%")};
  }
`;

const PaginationFooter = styled.footer`
  display: flex;
  justify-content: center;
  margin-top: 30px;

  @media (max-width: ${md}px) {
    height: 56px;
    margin-top: 0px;
    align-items: center;

    nav {
      padding: 14px 0px;
    }

    button {
      min-width: 28px;
      height: 28px;
      font-size: ${theme.fontSize.FONT_1};
      font-weight: ${theme.fontWeight.SEMI_BOLD};
      color: ${theme.color.neutral_500};
    }
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  padding: 100px;
  color: ${theme.color.neutral_500};
`;

export default ManageNok;
